import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (pw !== pw2) { toast.error("Those passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success("Password updated. Welcome back!");
      await refresh();
      nav("/program");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center p-6 bg-[#FFFFFF]">
      <div className="w-full max-w-sm">
        <Link to="/login" className="flex flex-col items-center gap-3 mb-8">
          <img src="/dftb26-mark.png" alt="" className="h-20 w-20 object-contain" />
          <img src="/dftb26-logo-dark.png" alt="DFTB26 - Back to Basics" className="h-12 w-auto object-contain" />
        </Link>
        <h1 className="font-display text-3xl mb-1 text-center">Choose a new password</h1>
        <p className="text-sm text-[#5a5a5a] mb-6 text-center">At least 6 characters. You&apos;ll be signed in right after.</p>

        {!token ? (
          <div className="p-4 bg-[#DC4F48]/10 border border-[#DC4F48]/30 rounded-sm text-sm text-[#1c1c24]" data-testid="reset-no-token">
            This link is missing the reset token. Request a fresh one from{" "}
            <Link to="/forgot-password" className="text-[#000000] hover:underline">Forgot password</Link>.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <div>
              <Label htmlFor="pw">New password</Label>
              <Input
                id="pw"
                type="password"
                required
                autoComplete="new-password"
                minLength={6}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="h-12 rounded-sm mt-1"
                data-testid="reset-pw-input"
              />
            </div>
            <div>
              <Label htmlFor="pw2">Confirm new password</Label>
              <Input
                id="pw2"
                type="password"
                required
                autoComplete="new-password"
                minLength={6}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="h-12 rounded-sm mt-1"
                data-testid="reset-pw2-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="reset-submit-btn"
              className="w-full h-12 rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24] font-semibold"
            >
              {loading ? "Updating…" : "Save new password"}
            </Button>
          </form>
        )}

        <p className="text-sm text-[#5a5a5a] text-center mt-6">
          <Link to="/login" className="text-[#000000] hover:underline" data-testid="reset-back-login">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
